import cn from 'classnames';
import { useRef, type FC } from 'react';
import { useHomePageContext } from '../HomePageContext';
import { TRADE } from 'dibs-buyer-layout/exports/userTypeEnums';
import { getSrcsetString } from 'dibs-image-utils/exports/srcSet';
import { trackEditorialImpressions, trackEditorialClick } from './helpers/EditorialTracking';

// components
import { Link } from 'dibs-elements/src/Link';
import { FormattedMessage } from 'dibs-react-intl';
import { InViewport } from 'dibs-in-viewport/exports/InViewport';

// styles
import dibsCss from 'dibs-css';
import styles from './HpSharedEditorialModuleContentPlacementVariant.module.css';

const consumerModules = [
    {
        image: 'https://a.1stdibscdn.com/dist/adhoc/homepage/consumer/promo_1_10-15-25.jpg',
        title: (
            <FormattedMessage
                id="homepage.HpSharedEditorialModuleContentPlacementVariant.consumer.module1.title"
                defaultMessage="Camille Charrière’s Vintage Obsession"
            />
        ),
        description: (
            <FormattedMessage
                id="homepage.HpSharedEditorialModuleContentPlacementVariant.consumer.module1.description"
                defaultMessage="The fashion influencer and writer handpicked vintage and estate jewels that can be interpreted in bold new ways."
            />
        ),
        cta: (
            <FormattedMessage
                id="homepage.HpSharedEditorialModuleContentPlacementVariant.consumer.module1.cta"
                defaultMessage="Shop the Collection"
            />
        ),
        link: '/collections/camille-charriere-curation/',
    },
    {
        image: 'https://a.1stdibscdn.com/dist/adhoc/homepage/consumer/promo_4_10-15-25.jpg',
        title: (
            <FormattedMessage
                id="homepage.HpSharedEditorialModuleContentPlacementVariant.consumer.module2.title"
                defaultMessage="The Holiday Gift Collection"
            />
        ),
        description: (
            <FormattedMessage
                id="homepage.HpSharedEditorialModuleContentPlacementVariant.consumer.module2.description"
                defaultMessage="Get a jump on the season with an extraordinary selection of gifts — and enjoy free shipping on many items."
            />
        ),
        cta: (
            <FormattedMessage
                id="homepage.HpSharedEditorialModuleContentPlacementVariant.consumer.module2.cta"
                defaultMessage="Explore Now"
            />
        ),
        link: '/collections/holiday-gift-collection/',
    },
    {
        image: 'https://a.1stdibscdn.com/dist/adhoc/homepage/consumer/promo_3_10-15-25.jpg',
        title: (
            <FormattedMessage
                id="homepage.HpSharedEditorialModuleContentPlacementVariant.consumer.module3.title"
                defaultMessage="A New Curation for Fall"
            />
        ),
        description: (
            <FormattedMessage
                id="homepage.HpSharedEditorialModuleContentPlacementVariant.consumer.module3.description"
                defaultMessage="From future heirlooms to unicorn pieces, shop new arrivals suffused with history and shaped by emerging trends."
            />
        ),
        cta: (
            <FormattedMessage
                id="homepage.HpSharedEditorialModuleContentPlacementVariant.consumer.module3.cta"
                defaultMessage="Shop Now"
            />
        ),
        link: '/collections/fall-catalogue/',
    },
];

const tradeModules = [
    {
        image: 'https://a.1stdibscdn.com/dist/adhoc/homepage/consumer/promo_1_10-15-25.jpg',
        title: (
            <FormattedMessage
                id="homepage.HpSharedEditorialModuleContentPlacementVariant.trade.module1.title"
                defaultMessage="Camille Charrière’s Vintage Obsession"
            />
        ),
        description: (
            <FormattedMessage
                id="homepage.HpSharedEditorialModuleContentPlacementVariant.trade.module1.description"
                defaultMessage="The fashion influencer and writer handpicked vintage and estate jewels that can be interpreted in bold new ways."
            />
        ),
        cta: (
            <FormattedMessage
                id="homepage.HpSharedEditorialModuleContentPlacementVariant.trade.module1.cta"
                defaultMessage="Shop the Collection"
            />
        ),
        link: '/collections/camille-charriere-curation/',
    },
    {
        image: 'https://a.1stdibscdn.com/dist/adhoc/homepage/consumer/promo_4_10-15-25.jpg',
        title: (
            <FormattedMessage
                id="homepage.HpSharedEditorialModuleContentPlacementVariant.trade.module2.title"
                defaultMessage="The Holiday Gift Collection"
            />
        ),
        description: (
            <FormattedMessage
                id="homepage.HpSharedEditorialModuleContentPlacementVariant.trade.module2.description"
                defaultMessage="Get a jump on the season with an extraordinary selection of gifts — and enjoy free shipping on many items."
            />
        ),
        cta: (
            <FormattedMessage
                id="homepage.HpSharedEditorialModuleContentPlacementVariant.trade.module2.cta"
                defaultMessage="Explore Now"
            />
        ),
        link: '/collections/holiday-gift-collection/',
    },
    {
        image: 'https://a.1stdibscdn.com/dist/adhoc/homepage/consumer/promo_3_10-15-25.jpg',
        title: (
            <FormattedMessage
                id="homepage.HpSharedEditorialModuleContentPlacementVariant.trade.module3.title"
                defaultMessage="A New Curation for Fall"
            />
        ),
        description: (
            <FormattedMessage
                id="homepage.HpSharedEditorialModuleContentPlacementVariant.trade.module3.description"
                defaultMessage="From future heirlooms to unicorn pieces, shop new arrivals suffused with history and shaped by emerging trends."
            />
        ),
        cta: (
            <FormattedMessage
                id="homepage.HpSharedEditorialModuleContentPlacementVariant.trade.module3.cta"
                defaultMessage="Shop Now"
            />
        ),
        link: '/collections/fall-catalogue/',
    },
];

export const HpSharedEditorialModuleContentPlacementVariant: FC<{ isMobile: boolean }> = ({
    isMobile,
}) => {
    const editorialModuleRef = useRef(null);
    const { userType } = useHomePageContext();
    const isTrade = userType === TRADE;
    const modules = isTrade ? tradeModules : consumerModules;
    const trackingItems = modules.map(module => ({ linkData: { path: module.link } }));

    const handleInitialDisplay = ({ inViewport }: { inViewport: boolean }): void => {
        if (trackingItems && inViewport) {
            trackEditorialImpressions(trackingItems);
        }
    };

    return (
        <InViewport stopWhenInViewport onInViewportChange={handleInitialDisplay}>
            <div ref={editorialModuleRef} className={cn(dibsCss.ptXlarge, dibsCss.pbLarger)}>
                <div
                    className={cn(dibsCss.textCenter, {
                        [dibsCss.mbSmall]: isMobile,
                        [dibsCss.mbLarge]: !isMobile,
                        [dibsCss.sassyFontHeaderLarge]: isMobile,
                        [dibsCss.sassyFontHeaderXLarge]: !isMobile,
                    })}
                >
                    <FormattedMessage
                        id="homepage.HpSharedEditorialModuleContentPlacementVariant.title"
                        defaultMessage="In the Spotlight"
                    />
                </div>
                <div
                    className={cn(dibsCss.flex, {
                        [styles.content]: !isMobile,
                        [dibsCss.flexCol]: isMobile,
                        [dibsCss.gapSmall]: isMobile,
                        [dibsCss.gapMedium]: !isMobile,
                    })}
                >
                    {modules.map((module, index) => (
                        <Link
                            key={module.link}
                            href={module.link}
                            className={cn(dibsCss.flex, dibsCss.flexCol, styles.text, {
                                [dibsCss.maxW33]: !isMobile,
                                [dibsCss.gapXsmall]: !isMobile,
                                [dibsCss.mbMedium]: isMobile,
                            })}
                            onClick={() =>
                                trackEditorialClick({ linkData: { path: module.link } }, index)
                            }
                        >
                            <img
                                alt=""
                                src={module.image}
                                srcSet={getSrcsetString(module.image)}
                                sizes="(max-width: 768px) 100vw, 33vw"
                                className={cn({
                                    [dibsCss.mbXsmall]: isMobile,
                                    [dibsCss.mbSmall]: !isMobile,
                                })}
                            />
                            <span
                                className={cn({
                                    [dibsCss.mbXsmall]: isMobile,
                                    [dibsCss.sassyFontHeaderLarge]: !isMobile,
                                    [dibsCss.sassyFontHeaderMedium]: isMobile,
                                })}
                            >
                                {module.title}
                            </span>
                            {!isMobile && (
                                <span className={dibsCss.sassyFontBodyMediumNormal}>
                                    {module.description}
                                </span>
                            )}
                            <span
                                className={cn(dibsCss.sassyFontBodyMediumNormal, styles.linkText)}
                            >
                                {module.cta}
                            </span>
                        </Link>
                    ))}
                </div>
            </div>
        </InViewport>
    );
};
